import { css } from '@emotion/core'
import { ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import { Button, ResponsiveTableRow, Separator, H5, DangerButton } from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveCheckboxTable } from '../../common/DmcResponsiveTable'
import { PersonLink, RelationshipLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import { DotSeparator } from '../../metadata/MetaData'
import PersonInfo from './PersonInfo'
import OwnerId from '../../common/OwnerId'
import AddRelationships from './AddRelationships'

const floatRight = css`
  float: right;
`

export default function SpousesPanel({
  handleRemoveSelected,
  handleRestoreSelected,
  currentPerson,
  onRemove,
  onRestore,
  accessControls,
  tombstonedCoupleRelationships,
  showTombstonedRelationships,
}) {
  const { isAuthorized } = useContext(PermissionsContext)
  const { t } = useTranslation()
  const spousesRelationshipsCount = currentPerson.relationships.spouses ? currentPerson.relationships.spouses.length : 0
  const tombstonedSpousesRelationshipsCount = tombstonedCoupleRelationships ? tombstonedCoupleRelationships.length : 0
  const [relationshipsToRemove, setRelationshipsToRemove] = useState([])
  const [relationshipsToRestore, setRelationshipsToRestore] = useState([])

  const showSeparator = () => {
    if (spousesRelationshipsCount > 0) {
      return true
    }
    if (tombstonedSpousesRelationshipsCount > 0 && showTombstonedRelationships) {
      return true
    }
    return false
  }

  return (
    <div>
      <H5>
        {t('person.relationships.spousesOf_plural', 'Spouses ({count}) of', {
          count: spousesRelationshipsCount + (showTombstonedRelationships ? tombstonedSpousesRelationshipsCount : 0),
        })}
        {': '}
        <PersonLink personId={currentPerson.id} personName={currentPerson.name} />
        <DotSeparator />
        {currentPerson.id}
      </H5>
      {isAuthorized('FtWritePermission') && spousesRelationshipsCount > 0 && relationshipsToRemove.length > 0 && (
        <DangerButton
          id="spouseCollectiveRemove"
          Icon={ContentDelete}
          onClick={() => handleRemoveSelected(relationshipsToRemove, 'couple')}
        >
          {t('action.removeSpouses', 'Remove ({count}) Spouses', {
            count: relationshipsToRemove.length,
          })}
        </DangerButton>
      )}
      {spousesRelationshipsCount > 0 && (
        <DmcResponsiveCheckboxTable
          id="spouseTable"
          totalColumnCount={4}
          onSelectionChange={setRelationshipsToRemove}
          atSizeColumns={{
            default: {
              labels: [
                t('spouse', 'Spouse'),
                t('owner', 'Owner'),
                t('person.relationships.relationshipid', 'Relationship ID'),
                '',
              ],
              columnWidths: [4, 4, 2, 2],
            },
          }}
        >
          {currentPerson.relationships.spouses &&
            currentPerson.relationships.spouses.map((relationship) => {
              return (
                <ResponsiveTableRow
                  name={relationship.relationshipId}
                  key={relationship.relationshipId}
                  column1={
                    relationship && (
                      <PersonInfo
                        key={relationship.spouse.personId}
                        person={relationship.spouse}
                        accessControls={accessControls}
                      />
                    )
                  }
                  column2={
                    relationship && (
                      <OwnerId key={relationship.spouse.personId} ownerInfo={relationship.spouse?.ownerInfo} />
                    )
                  }
                  column3={<RelationshipLink relationshipId={relationship.relationshipId} relationshipType="couple" />}
                  column4={
                    <span css={floatRight}>
                      <DangerButton
                        id={`spouseRemoveBtn_${relationship.relationshipId}`}
                        Icon={ContentDelete}
                        onClick={() => onRemove(relationship.relationshipId, 'couple')}
                        disabled={!isAuthorized('FtWritePermission')}
                      >
                        {t('action.remove', 'Remove')}
                      </DangerButton>
                    </span>
                  }
                />
              )
            })}
        </DmcResponsiveCheckboxTable>
      )}
      {showTombstonedRelationships && tombstonedCoupleRelationships && tombstonedSpousesRelationshipsCount > 0 && (
        <>
          <H5 supplementary>
            {t('person.relationships.tombstoned_plural', 'Tombstoned ({count})', {
              count: tombstonedSpousesRelationshipsCount,
            })}
          </H5>
          {isAuthorized('FtWritePermission') &&
            tombstonedSpousesRelationshipsCount > 0 &&
            relationshipsToRestore.length > 0 && (
              <Button
                id="spouseCollectiveRestore"
                emphasis="medium"
                onClick={() => handleRestoreSelected(relationshipsToRestore, 'couple')}
              >
                {t('action.restoreSpouses', 'Restore ({count}) Spouses', { count: relationshipsToRestore.length })}
              </Button>
            )}
          <DmcResponsiveCheckboxTable
            id="spouseRestoreTable"
            totalColumnCount={4}
            onSelectionChange={setRelationshipsToRestore}
            atSizeColumns={{
              default: {
                labels: [
                  t('spouse', 'Spouse'),
                  t('owner', 'Owner'),
                  t('person.relationships.relationshipid', 'Relationship ID'),
                  '',
                ],
                columnWidths: [4, 4, 2, 2],
              },
            }}
          >
            {tombstonedCoupleRelationships.map((relationship) => {
              const spouse =
                relationship.spouse1.personId === currentPerson.id ? relationship.spouse2 : relationship.spouse1
              return (
                <ResponsiveTableRow
                  id={`spouse_${relationship.relationshipId}`}
                  name={relationship.relationshipId}
                  key={relationship.relationshipId}
                  column1={
                    relationship && <PersonInfo key={spouse.personId} person={spouse} accessControls={accessControls} />
                  }
                  column2={relationship && <OwnerId key={spouse.personId} ownerInfo={spouse?.ownerInfo} />}
                  column3={<RelationshipLink relationshipId={relationship.relationshipId} relationshipType="couple" />}
                  column4={
                    <span css={floatRight}>
                      <Button
                        id={`spouseRestoreBtn_${relationship.relationshipId}`}
                        emphasis="medium"
                        onClick={() => onRestore(relationship.relationshipId, 'couple')}
                        disabled={!isAuthorized('FtWritePermission')}
                      >
                        {t('action.restore', 'Restore')}
                      </Button>
                    </span>
                  }
                />
              )
            })}
          </DmcResponsiveCheckboxTable>
        </>
      )}
      <AddRelationships relationshipType="spouses" />
      {showSeparator() && <Separator size="xl" />}
    </div>
  )
}
